import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { lmsAdmin } from '../api'
import { logoFor, domainOf, relativeTime, accessLevelLabel, restrictWarning } from '../format'
import { AddIntegrationModal } from './add-integration-modal'
import { CopyLine } from '../copy-line'
import { CourseScoringDialog } from './course-scoring-dialog'
import { CourseResultRule } from './course-result-rule'
import { describeScoring } from '../score-reporting'
import { useRoleOrg } from '../../../role/hook/role-org'
import { ROLE_ORG } from '../../../role/role.context'
import '../lms-admin.scss'
import { copyText } from '../../../shared/clipboard/clipboard'
import { LmsIcon } from '../lms-icon'
import { LmsTrail, lmsTrail } from '../lms-trail'
import { ConfirmDialog } from '../../../shared/sso-workspace/confirm-dialog'
import { useErrorLine } from '../../../shared/error/use-error-line'
import { SECTION } from '../../../shared/navigation/sections'
import { IconCourse } from '../../../shared/icon/icon'
import '../../../shared/sso-workspace/sso-workspace.scss'

// Course → LMS Integration (docs/08 §9, design
// edxr-course-lms-integration-v1.0.html). Manages which LMS connections a
// course is published to. Artifact states: zero, active, stale (banner +
// per-row Update available), no-package rows. Builds are synchronous in v1
// (1-3s), so the artifact's "generating" progress strip has no backing
// state to render.

const shortSha = sha => (sha || '').slice(0, 8)

// A course on an LTI 1.3 connection (Phase L Task 8, L-11) has no package: the
// LMS's own activity launches it, and names the course by a custom parameter,
// edxr_course=<course id>. Choosing the course with the LMS's "Select content"
// (Deep Linking) fills the parameter in; otherwise the admin pastes this line.
const isLti = connection => !!connection && connection.delivery === 'lti13'

// An LTI 1.3 connection made but not yet given the LMS's details (the service's
// lti_registered is false) accepts no launch. Only a flag that says false counts:
// an answer without it (a service older than the flag) is read as registered.
const isWaitingForLms = connection => isLti(connection) && connection.lti_registered === false && connection.status !== 'archived'

// An LTI row's pill: whether the connection launches at all.
const LTI_PILL = {
  active: { className: 'success', label: 'Active', pulse: true },
  paused: { className: 'warning', label: 'Paused', pulse: false },
  archived: { className: 'neutral', label: 'Archived', pulse: false }
}
const WAITING_PILL = { className: 'warning', label: "Waiting for the LMS's details", pulse: false }

export const ViewCourseIntegration = () => {
  const history = useHistory()
  const { nodeId } = useParams()
  // Changing a course's score setting is the LMS admin scope's (create and
  // delete, as the connection's API keys panel reads it); the service checks.
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const isLmsAdmin = !!lms.accessCreate && !!lms.accessDelete

  const [course, updateCourse] = useState(null)
  const [integrations, updateIntegrations] = useState(null)
  const [error, updateError] = useState(null)
  const [busy, updateBusy] = useState({})
  const [restricting, updateRestricting] = useState({})
  const [notRestricted, updateNotRestricted] = useState(null)
  const [showAdd, updateShowAdd] = useState(false)
  // The integration whose score reporting is being changed.
  const [scoringFor, updateScoringFor] = useState(null)
  const [removing, updateRemoving] = useState(null)
  const [removeBusy, updateRemoveBusy] = useState(false)
  const [removeError, removeFailure, setRemoveError] = useErrorLine()

  const load = async () => {
    try {
      const result = await lmsAdmin.listIntegrations(nodeId)
      updateCourse(result.course || null)
      updateIntegrations(result.integrations || [])
      updateError(null)
    } catch (e) {
      updateError(e)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId])

  const rows = useMemo(() => (integrations || []).filter(view => view.connection), [integrations])
  // A package that is out of date: an LTI connection has none.
  const packageRows = useMemo(() => rows.filter(view => !isLti(view.connection)), [rows])
  const staleRows = useMemo(() => packageRows.filter(view => view.stale), [packageRows])
  const mappedConnectionIds = useMemo(() => rows.map(view => view.connection.id), [rows])

  const rebuild = async view => {
    const connectionId = view.connection.id
    updateBusy(prev => ({ ...prev, [connectionId]: true }))
    try {
      await lmsAdmin.buildPackage(connectionId, Number(nodeId))
      await load()
      updateError(null)
    } catch (e) {
      updateError(e)
    } finally {
      updateBusy(prev => ({ ...prev, [connectionId]: false }))
    }
  }

  const regenerateAll = async () => {
    for (const view of staleRows) {
      await rebuild(view)
    }
  }

  const download = async view => {
    try {
      const url = await lmsAdmin.packageDownloadURL(view.current_package.id)
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      updateError(e)
    }
  }

  const copyPackageInfo = view => {
    const pkg = view.current_package
    copyText(`package #${pkg.id} sha256 ${pkg.sha256}`)
  }

  // Remove asks first, in the workspace's own dialog; a refusal keeps it open
  // with the reason.
  const remove = view => {
    setRemoveError(null)
    updateRemoving(view)
  }

  const confirmRemove = async () => {
    updateRemoveBusy(true)
    setRemoveError(null)
    try {
      await lmsAdmin.unmapCourse(removing.connection.id, Number(nodeId))
      updateRemoving(null)
      await load()
    } catch (e) {
      setRemoveError(e.message || "The course couldn't be removed from this LMS.", e)
    }
    updateRemoveBusy(false)
  }

  // Make the course "Only assigned": the same idempotent map call the Add LMS
  // modal makes (mapping a course that is already mapped changes nothing but the
  // restrict). When it did not apply the admin is told why and can try again.
  const restrict = async view => {
    const connectionId = view.connection.id
    updateRestricting(prev => ({ ...prev, [connectionId]: true }))
    updateNotRestricted(null)
    try {
      const mapping = await lmsAdmin.mapCourse(connectionId, {
        node_id: Number(nodeId),
        restrict_to_lms_learners: true
      })
      updateNotRestricted(restrictWarning(mapping))
      await load()
    } catch (e) {
      updateError(e)
    } finally {
      updateRestricting(prev => ({ ...prev, [connectionId]: false }))
    }
  }

  const subline = () => {
    if (rows.length === 0) return 'Generate SCORM launchers to deliver this course through your LMS.'
    const ltiCount = rows.length - packageRows.length
    if (packageRows.length === 0) {
      return `${ltiCount} LMS connection${ltiCount === 1 ? ' delivers' : 's deliver'} this course through LTI 1.3.`
    }
    const throughLti = ltiCount > 0 ? ` ${ltiCount} more deliver${ltiCount === 1 ? 's' : ''} it through LTI 1.3.` : ''
    if (staleRows.length > 0) {
      return `${staleRows.length} of ${packageRows.length} launcher${packageRows.length === 1 ? ' was' : 's were'} built before the latest course update.${throughLti}`
    }
    return `${packageRows.length} LMS connection${packageRows.length === 1 ? ' has' : 's have'} an active SCORM launcher for this course.${throughLti}`
  }

  return (
    <div className="lms-admin" data-testid="course-integration">
      <LmsTrail
        trail={lmsTrail(
          SECTION.lmsCourses,
          { url: `/org/lms/courses/${nodeId}/integration`, text: course ? course.name : '', Icon: IconCourse }
        )}
      />
      <div className="page-header-accent">
        <h1>{course ? course.name : ''}</h1>
        <div className="meta">
          <span className="text-mono">node {nodeId}</span>
          {course && course.access_level && (
            <React.Fragment>
              <span style={{ margin: '0 6px', opacity: 0.5 }}>·</span>
              <span>{accessLevelLabel(course.access_level)} course</span>
            </React.Fragment>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error.code === 'FEATURE_DISABLED'
            ? "The LMS Connector isn't enabled for this organization yet — contact EducationXR to turn it on."
            : `LMS integration request failed: ${error.message}`}
        </div>
      )}

      {notRestricted && (
        <div className="alert alert-warning" role="alert" data-testid="restrict-warning">
          {notRestricted}
        </div>
      )}

      {integrations && (
        <div className="lms-card">
          <div className="lms-card-head">
            <div className="icon">
              <LmsIcon name="link" />
            </div>
            <div className="body">
              <h2>LMS Integration</h2>
              <div className="sub">{subline()}</div>
            </div>
            {rows.length > 0 && (
              <div className="actions">
                <button className="btn btn-light border btn-sm" type="button" data-testid="add-lms" onClick={() => updateShowAdd(true)}>
                  <LmsIcon name="plus" className="me-1" />
                  Add LMS
                </button>
              </div>
            )}
          </div>

          {staleRows.length > 0 && (
            <div className="stale-banner" data-testid="stale-banner">
              <LmsIcon name="circle-exclamation" />
              <div className="body">
                <strong>Course updated since some launchers were built.</strong> Regenerating refreshes launcher metadata
                and applies the latest connection settings. Active learner sessions won't be interrupted; regenerated
                launchers apply on the next LMS launch.
              </div>
              <button className="btn-regenerate" type="button" data-testid="regenerate-all" onClick={regenerateAll}>
                Regenerate all
              </button>
            </div>
          )}

          {rows.length === 0 && (
            <div className="lms-zero" data-testid="integration-zero">
              <div className="zero-icon">
                <LmsIcon name="folder-open" regular />
              </div>
              <h3>No SCORM packages yet</h3>
              <p>
                Generate a SCORM package for one of your{' '}
                <a
                  href="#/"
                  onClick={e => {
                    e.preventDefault()
                    history.push('/org/lms/connections')
                  }}
                >
                  LMS connections
                </a>{' '}
                to make this course available to learners through your LMS.
              </p>
              <button className="btn btn-primary btn-sm" type="button" data-testid="zero-add-lms" onClick={() => updateShowAdd(true)}>
                <LmsIcon name="plus" className="me-1" />
                Generate package
              </button>
            </div>
          )}

          {rows.length > 0 && (
            <ul className="integration-list">
              {rows.map(view => {
                const connection = view.connection
                const pkg = view.current_package
                const logo = logoFor(connection.preset && connection.preset.name)
                const lti = isLti(connection)
                const waiting = isWaitingForLms(connection)
                // Waiting replaces Active only: a paused or archived connection says so first.
                const ltiPill = waiting && connection.status === 'active' ? WAITING_PILL : LTI_PILL[connection.status] || LTI_PILL.archived
                const built = pkg && relativeTime(pkg.built_at)
                const isBusy = !!busy[connection.id]
                // An xAPI record store has no LMS learners to keep access, and an
                // archived connection launches nobody (the server refuses both).
                const canRestrict =
                  course && course.access_level === 'private' && connection.delivery !== 'xapi-lrs' && connection.status !== 'archived'
                // The setting always shows; Change is for the LMS admin scope, and
                // an archived connection refuses it (the server does too).
                const canChangeScoring = isLmsAdmin && connection.status !== 'archived'
                return (
                  <li className="integration-row" key={view.mapping.id} data-testid={`integration-${connection.id}`}>
                    <div className={`conn-logo ${logo.className}`}>{logo.text}</div>
                    <div className="info">
                      <div className="name-line">
                        <span className="name">{connection.name}</span>
                        {lti && (
                          <span className={`pill ${ltiPill.className}`}>
                            <span className={`dot ${ltiPill.pulse ? 'pulse' : ''}`} />
                            {ltiPill.label}
                          </span>
                        )}
                        {!lti && !pkg && (
                          <span className="pill neutral">
                            <span className="dot" />
                            No package
                          </span>
                        )}
                        {!lti && pkg && view.stale && (
                          <span className="pill warning">
                            <span className="dot" />
                            Update available
                          </span>
                        )}
                        {!lti && pkg && !view.stale && (
                          <span className="pill success">
                            <span className="dot pulse" />
                            Active
                          </span>
                        )}
                      </div>
                      <div className="domain">{domainOf(connection.lms_base_url)}</div>
                      <div className="meta-row">
                        {lti ? (
                          <span className="item">
                            <LmsIcon name="plug" />
                            LTI 1.3 · no package to build
                          </span>
                        ) : pkg ? (
                          <React.Fragment>
                            <span className="item">
                              <LmsIcon name="box" />
                              Package <span className="v">#{pkg.id} · {shortSha(pkg.sha256)}</span>
                            </span>
                            {built && (
                              <React.Fragment>
                                <span className="sep">·</span>
                                <span className="item">
                                  <LmsIcon name="clock-rotate-left" />
                                  Built {built.text}
                                </span>
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        ) : (
                          <span className="item">
                            <LmsIcon name="circle-info" />
                            No package built yet — build one to enable LMS launches.
                          </span>
                        )}
                      </div>
                      {waiting && (
                        <div className="field-help" style={{ marginTop: 6 }} data-testid={`lti-course-waiting-${connection.id}`}>
                          Learners can't open this course from the LMS until the connection has the LMS's details. Copy them from
                          your LMS into the connection's page.
                        </div>
                      )}
                      {lti && (
                        <div data-testid={`lti-course-${connection.id}`} style={{ marginTop: 8 }}>
                          <CopyLine
                            testId={`edxr-course-${connection.id}`}
                            label="Custom parameter for the activity in your LMS"
                            value={`edxr_course=${nodeId}`}
                          />
                          <div className="field-help" style={{ marginTop: 0 }} data-testid={`lti-course-note-${connection.id}`}>
                            Choosing this course with "Select content" in your LMS (Deep Linking) fills it in for you. To add
                            the activity by hand, paste this line into the activity's custom parameters.
                          </div>
                        </div>
                      )}
                      {view.mapping.effective_scoring && (
                        <div className="meta-row" data-testid={`scoring-${connection.id}`}>
                          <span className="item">
                            <LmsIcon name="list-check" />
                            Score and pass: <span className="v">{describeScoring(view.mapping.effective_scoring)}</span>
                            <span className="text-muted ms-1">{view.mapping.scoring ? '(this course)' : '(preset)'}</span>
                          </span>
                          {canChangeScoring && (
                            <button
                              className="btn btn-link btn-sm p-0 ms-2"
                              type="button"
                              data-testid={`scoring-edit-${connection.id}`}
                              onClick={() => updateScoringFor(view)}
                            >
                              Change
                            </button>
                          )}
                        </div>
                      )}
                      {Object.prototype.hasOwnProperty.call(view.mapping, 'result_rule') && (
                        <CourseResultRule
                          connection={connection}
                          nodeId={nodeId}
                          mapping={view.mapping}
                          canChange={canChangeScoring}
                          onSaved={load}
                        />
                      )}
                    </div>
                    <div className="actions">
                      {canRestrict && (
                        <button
                          className="btn btn-light border btn-sm"
                          type="button"
                          disabled={!!restricting[connection.id]}
                          data-testid={`restrict-${connection.id}`}
                          title="Make this course only assigned: people in this organization who are not assigned lose it; this connection's learners keep it"
                          onClick={() => restrict(view)}
                        >
                          Restrict to LMS learners
                        </button>
                      )}
                      {!lti && pkg && (
                        <React.Fragment>
                          <button className="icon-btn" title="Download .zip" type="button" data-testid={`download-${connection.id}`} onClick={() => download(view)}>
                            <LmsIcon name="download" />
                          </button>
                          <button className="icon-btn" title="Copy package info" type="button" onClick={() => copyPackageInfo(view)}>
                            <LmsIcon name="copy" regular />
                          </button>
                          <button
                            className="icon-btn"
                            title="View build log"
                            type="button"
                            data-testid={`build-log-${connection.id}`}
                            onClick={() => history.push(`/org/lms/packages/${pkg.id}/build-log`, { course: { nodeId, name: course ? course.name : '' } })}
                          >
                            <LmsIcon name="terminal" />
                          </button>
                        </React.Fragment>
                      )}
                      {!lti && (
                        <button
                          className={`icon-btn ${!pkg || view.stale ? 'primary-icon' : ''}`}
                          title={pkg ? 'Regenerate launcher' : 'Build package'}
                          type="button"
                          disabled={isBusy}
                          data-testid={`rebuild-${connection.id}`}
                          onClick={() => rebuild(view)}
                        >
                          <LmsIcon name="rotate" spin={isBusy} />
                        </button>
                      )}
                      <button className="icon-btn" title="Remove from this LMS" type="button" onClick={() => remove(view)}>
                        <LmsIcon name="trash-can" regular />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          <div className="lms-card-foot" data-testid="integration-foot">
            <div className="left">
              {packageRows.length > 0 && (
                <span className="item" data-testid="integration-foot-note">
                  <LmsIcon name="circle-info" />
                  A launcher shows Update available when the course has changed since it was built. Regenerate it to bring
                  it up to date.
                </span>
              )}
            </div>
            <a
              href="#/"
              className="btn btn-link btn-sm lms-foot-link"
              data-testid="view-connections-link"
              onClick={e => {
                e.preventDefault()
                history.push('/org/lms/connections')
              }}
            >
              View connections<LmsIcon name="arrow-right" className="ms-1" style={{ fontSize: 9 }} />
            </a>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(removing)}
        title={removing ? `Remove this course from ${removing.connection.name}?` : ''}
        body="LMS learners will no longer be able to launch it from that LMS."
        confirmText="Remove course"
        busy={removeBusy}
        error={removeError}
        failure={removeFailure}
        onConfirm={confirmRemove}
        onCancel={() => updateRemoving(null)}
      />

      {scoringFor && (
        <CourseScoringDialog
          connection={scoringFor.connection}
          nodeId={nodeId}
          mapping={scoringFor.mapping}
          onClose={() => updateScoringFor(null)}
          onSaved={async () => {
            updateScoringFor(null)
            await load()
          }}
        />
      )}

      {showAdd && course && (
        <AddIntegrationModal
          course={course}
          mappedConnectionIds={mappedConnectionIds}
          onClose={() => updateShowAdd(false)}
          onAdded={async () => {
            updateShowAdd(false)
            await load()
          }}
        />
      )}
    </div>
  )
}
